import React, {
  useState,
  useContext,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import agent, { LaunchableFromList } from "server";
import { HardDrive, Rocket, Settings2, ExternalLink } from "lucide-react";
import { Button } from "components/Graphs/Button";
import { Badge } from "components/Graphs/Badge";
import { BrowseLaunchables } from "./BrowseLaunchables";
import ComputeSelector from "../ComputeSelector/index";
import { OrgContext } from "contexts/OrgContext";
import { CloudCredContext } from "contexts/CloudCredContext";
import { InstanceTypesContext } from "contexts/InstanceTypesContext";
import { v4 as uuid } from "uuid";
import { createGpuCpuEnvironmentPlaceholderPair } from "utils/defaultEnvironmentDisplayName";
import {
  GPUInstanceType,
  GetProviderName,
  Location,
} from "types/InstanceTypes";
import DeploymentSection from "../DeploymentSection";
import FilterBar from "../ComputeSelector/Advanced/FilterBar/index";
import {
  NO_REGION_FILTER,
  RegionFilterState,
  usePickerAllowedLocations,
} from "../ComputeSelector/Advanced/FilterBar/RegionFilter";
import { validateName, regionToReadable, deriveGeo } from "components/utils";
import { handleIsPaymentSetup, allPublicCloudsName } from "../utils/index";
import { BillingContext } from "contexts/BillingContext";
import { AddCreditsModal } from "components/ConnectCloudsOrCardModal/AddCreditsModal";
import ConnectCloudsOrCardModal from "components/ConnectCloudsOrCardModal";
import { ContainerSelected } from "../../shared/BuildTypes";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "components/Graphs/Tabs";
import {
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "components/Graphs/Drawer";
import DrawerContainerSelector from "../DrawerContainerSelector/index";
import { getInstanceLocationsMatching, isBrevCloud } from "utils/instanceTypes";
import { UserContext } from "contexts/UserContext";
import { getInstanceCloudCreds, isLaunchpadInstance } from "utils/cloudCreds";
import {
  isFreeTierInstance,
  getBasePriceAmount,
  getStoragePricing,
  useFreeTier,
} from "utils/freeTierUtils";
import { MapPinIcon } from "@heroicons/react/24/outline";
import { InfoTooltip } from "components/Graphs/InfoTooltip";
import { Input } from "components/Graphs/Input";
import { Checkbox } from "components/Graphs/Checkbox";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "components/Graphs/Popover";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import {
  getCreateEnvironmentUrlStateFromLaunchable,
  getCreateEnvironmentUrlStateFromContainer,
  getGpuQueryValueForInstance,
  getInitialContainerFromCreateEnvironmentUrl,
  parseCreateEnvironmentSearch,
  CreateEnvironmentUrlUpdates,
  FeaturedContainerUrlParams,
} from "./createEnvironmentUrlState";
import { useCreateEnvironmentNavigation } from "./useCreateEnvironmentNavigation";
import { useApplicationShellTerminology } from "components/ApplicationShell/terminology";
import { DEFAULT_LIFECYCLE_SCRIPT } from "../LifeCycleScript/index";

export const UnifiedCreate: React.FC = () => {
  const terminology = useApplicationShellTerminology();
  const orgContext = useContext(OrgContext);
  const { cloudCreds } = useContext(CloudCredContext);
  const instanceTypesContext = useContext(InstanceTypesContext);
  const userContext = useContext(UserContext);
  const billingContext = useContext(BillingContext);
  const {
    routerLocation,
    replaceCreateEnvironmentSearch,
    pushCreateEnvironmentSearch,
    returnToComputePicker,
    shouldRestoreSoftwareFromUrl,
  } = useCreateEnvironmentNavigation();

  const queryState = useMemo(
    () => parseCreateEnvironmentSearch(routerLocation.search),
    [routerLocation.search],
  );
  const instanceFromQuery = queryState.instanceType;
  const locationParam = queryState.location;

  // Unified UI State
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState<"configure" | "browse">(
    "configure",
  );

  // Build Custom State - centralized in container object
  const [container, setContainer] = useState<ContainerSelected>(() =>
    getInitialContainerFromCreateEnvironmentUrl(queryState),
  );
  const [tempDrawerContainer, setTempDrawerContainer] =
    useState<ContainerSelected | null>(null);

  // Launchables State
  const [selectedLaunchableId, setSelectedLaunchableId] = useState<
    string | null
  >(queryState.launchableID || null);
  const [selectedLaunchable, setSelectedLaunchable] =
    useState<LaunchableFromList | null>(null);

  useEffect(() => {
    let cancelled = false;

    const fetchLaunchable = async () => {
      if (selectedLaunchableId) {
        const res =
          await agent.Launchables.getPublicLaunchable(selectedLaunchableId);
        if (res.success && !cancelled) {
          setSelectedLaunchable(res.data);
          // Map launchable build request to container state
          const buildReq = res.data.buildRequest;
          if (buildReq) {
            setContainer({
              vmBuild: buildReq.vmBuild,
              // verbBuild: buildReq.verbBuild, // Type mismatch potential, handle carefully if needed
              // customContainer: buildReq.containerBuild,
              dockerCompose: buildReq.dockerCompose,
            });
          }
          // Auto-populate environment name
          if (res.data.name) {
            const normalizedName = res.data.name
              .replace(/[^a-zA-Z0-9]/g, "-")
              .toLowerCase();
            const randomString = uuid().substring(0, 6);
            setEnvironmentName(`${normalizedName}-${randomString}`);
          }
        }
      } else if (!cancelled) {
        setSelectedLaunchable(null);
      }
    };
    fetchLaunchable();

    return () => {
      cancelled = true;
    };
  }, [selectedLaunchableId]);

  // Compute State
  const [selectedInstanceType, setSelectedInstanceType] = useState<
    GPUInstanceType | undefined
  >();
  const [storage, setStorage] = useState(queryState.storage || "256");
  const [displayGPUInstances, setDisplayGPUInstances] = useState<
    GPUInstanceType[] | undefined
  >(undefined);
  const [displayCPUInstances, setDisplayCPUInstances] = useState<
    GPUInstanceType[] | undefined
  >(undefined);
  const [selectedCloudName, setSelectedCloudName] =
    useState(allPublicCloudsName);

  // Filter State
  const [persistentOnly, setPersistentOnly] = useState<boolean>(false);
  const [flexiblePortControlOnly, setFlexiblePortControlOnly] =
    useState<boolean>(false);
  const [flexibleStorageOnly, setFlexibleStorageOnly] =
    useState<boolean>(false);

  // Location & Instance Count State
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(
    null,
  );
  const [regionFilter, setRegionFilter] =
    useState<RegionFilterState>(NO_REGION_FILTER);
  const pickerAllowedLocations = usePickerAllowedLocations(
    regionFilter,
    selectedLocation?.name ?? null,
  );
  const [instanceCount, setInstanceCount] = useState<number>(1);
  const [useCustomRegion, setUseCustomRegion] = useState<boolean>(
    queryState.customRegion === true,
  );
  const [customRegionValue, setCustomRegionValue] = useState<string>(
    queryState.customRegion && queryState.location !== "any"
      ? queryState.location || ""
      : "",
  );
  const [regionSearch, setRegionSearch] = useState<string>("");
  const [regionPopoverOpen, setRegionPopoverOpen] = useState<boolean>(false);

  const allInstanceTypes = useMemo(
    () => [
      ...instanceTypesContext.GPUInstancesAvailable,
      ...instanceTypesContext.CPUInstancesAvailable,
    ],
    [
      instanceTypesContext.GPUInstancesAvailable,
      instanceTypesContext.CPUInstancesAvailable,
    ],
  );

  const isLocationAvailableForInstance = useCallback(
    (instance: GPUInstanceType, location: string) =>
      allInstanceTypes
        .filter((inst) => inst.type === instance.type)
        .some((inst) => {
          const locationNames = inst.available_locations?.[0]
            ? inst.available_locations
            : inst.location
              ? [inst.location]
              : [];
          return locationNames.includes(location);
        }),
    [allInstanceTypes],
  );

  // Compute available locations from all instances of the same type
  const availableLocations = useMemo(() => {
    if (!selectedInstanceType) {
      return [];
    }

    // Find all instances of the same type (check both GPU and CPU pools)
    const sameTypeInstances = [
      ...instanceTypesContext.GPUInstancesAvailable,
      ...instanceTypesContext.CPUInstancesAvailable,
    ].filter((inst) => inst.type === selectedInstanceType.type);

    // Collect unique locations from all instances of this type
    const locationMap = new Map<string, Location>();

    for (const inst of sameTypeInstances) {
      if (!inst.location) continue;

      const locationsNames = inst.available_locations?.[0]
        ? inst.available_locations
        : [inst.location];
      locationsNames.forEach((locName) => {
        if (!locationMap.has(locName)) {
          // Try to find matching Location metadata from cloud credential locations.
          let locMeta: Location | null = null;
          for (const cloudCred of getInstanceCloudCreds(inst)) {
            const found = cloudCred.locations?.find((l) => l.name === locName);
            if (found) {
              locMeta = found;
              break;
            }
          }

          locationMap.set(
            locName,
            locMeta || { name: locName, description: locName },
          );
        }
      });
    }

    return Array.from(locationMap.values()).sort((a, b) => {
      const aLabel = regionToReadable[a.name]?.city || a.description || a.name;
      const bLabel = regionToReadable[b.name]?.city || b.description || b.name;
      return aLabel.localeCompare(bLabel);
    });
  }, [
    selectedInstanceType,
    instanceTypesContext.GPUInstancesAvailable,
    instanceTypesContext.CPUInstancesAvailable,
  ]);

  // Group locations by geo and filter by search query
  const groupedLocations = useMemo(() => {
    const q = regionSearch.toLowerCase().trim();
    const filtered = q
      ? availableLocations.filter((loc) => {
          const info = regionToReadable[loc.name];
          if (!info) return loc.name.toLowerCase().includes(q);
          return (
            info.city.toLowerCase().includes(q) ||
            info.country.toLowerCase().includes(q) ||
            info.geo.toLowerCase().includes(q) ||
            loc.name.toLowerCase().includes(q)
          );
        })
      : availableLocations;

    const groups = new Map<string, Location[]>();
    for (const loc of filtered) {
      const info = regionToReadable[loc.name];
      const geo = info?.geo || deriveGeo(info?.country || "Unknown");
      if (!groups.has(geo)) groups.set(geo, []);
      groups.get(geo)!.push(loc);
    }

    // Sort geos alphabetically, locations within each geo alphabetically by city
    return [...groups.entries()]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([geo, locs]) => ({
        geo,
        locations: locs.sort((a, b) => {
          const aCity = regionToReadable[a.name]?.city || a.name;
          const bCity = regionToReadable[b.name]?.city || b.name;
          return aCity.localeCompare(bCity);
        }),
      }));
  }, [availableLocations, regionSearch]);

  // Reset selected location when instance type changes, but preserve from query param if available
  useEffect(() => {
    if (locationParam && locationParam !== "any" && queryState.customRegion) {
      setUseCustomRegion(true);
      setCustomRegionValue(locationParam);
      setSelectedLocation({
        name: locationParam,
        description: locationParam,
      });
    } else if (locationParam && locationParam !== "any") {
      const found = availableLocations.find((l) => l.name === locationParam);
      if (found) {
        setUseCustomRegion(false);
        setCustomRegionValue("");
        setSelectedLocation(found);
      } else {
        setSelectedLocation(null);
      }
    } else {
      setUseCustomRegion(false);
      setCustomRegionValue("");
      setSelectedLocation(null);
    }
  }, [availableLocations, locationParam, queryState.customRegion]);

  // Deployment State — new GPU/CPU placeholders each time this create flow mounts
  const [environmentNamePlaceholders] = useState(
    createGpuCpuEnvironmentPlaceholderPair,
  );
  const [environmentName, setEnvironmentName] = useState(
    () => environmentNamePlaceholders.gpu,
  );
  const [nameFieldErrorMessage, setNameFieldErrorMessage] =
    useState<string>("");

  useEffect(() => {
    if (selectedInstanceType) {
      const isCPU =
        !selectedInstanceType.supported_gpus ||
        selectedInstanceType.supported_gpus.length === 0;
      const newDefault = isCPU
        ? environmentNamePlaceholders.cpu
        : environmentNamePlaceholders.gpu;
      setEnvironmentName((prev) =>
        prev === environmentNamePlaceholders.gpu ||
        prev === environmentNamePlaceholders.cpu
          ? newDefault
          : prev,
      );
    }
  }, [selectedInstanceType, environmentNamePlaceholders]);
  const selectedInstanceHasGpu = !!selectedInstanceType?.supported_gpus?.length;
  const isLaunchpad = isLaunchpadInstance(selectedInstanceType);
  const [loading, setLoading] = useState(false);
  const [showFinalDeployMessage, setShowFinalDeployMessage] = useState(false);
  const [portMappings, setPortMapping] = useState({});
  const shouldHydrateDefaultFeaturedContainerVersions =
    queryState.buildMode === "featured-container" &&
    (queryState.featuredContainer || "Default") === "Default";
  const [pythonVersion, setPythonVersion] = useState<string>(
    shouldHydrateDefaultFeaturedContainerVersions
      ? queryState.pythonVersion || "3.10"
      : "3.10",
  );
  const [cudaVersion, setCudaVersion] = useState<string>(
    shouldHydrateDefaultFeaturedContainerVersions
      ? queryState.cudaVersion || "12.0.1"
      : "12.0.1",
  );
  const [lifecycleScriptTab, setLifecycleScriptTab] = useState<
    "upload" | "paste"
  >("paste");
  const [lifecycleScriptFile, setLifecycleScriptFile] = useState<File | null>(
    null,
  );
  const [autoFocusScript, setAutoFocusScript] = useState(false);

  // Modal State
  const [showConnectCloudsOrCardModal, setShowConnectCloudsOrCardModal] =
    useState<boolean>(false);
  const [noCardOnFile, setNoCardOnFile] = useState<boolean>(false);
  const [inlineAlertMessage, setInlineAlertMessage] = useState("");
  const [inlineAlertSeverity, setInlineAlertSeverity] = useState<
    "error" | "warning" | "info" | "success"
  >("error");

  useEffect(() => {
    if (!shouldRestoreSoftwareFromUrl) {
      return;
    }

    const restoredLaunchableId = queryState.buildMode
      ? null
      : queryState.launchableID || null;
    if (
      !restoredLaunchableId ||
      restoredLaunchableId !== selectedLaunchableId
    ) {
      setContainer(getInitialContainerFromCreateEnvironmentUrl(queryState));
    }
    if (restoredLaunchableId !== selectedLaunchableId) {
      setSelectedLaunchable(null);
      setSelectedLaunchableId(restoredLaunchableId);
    }
    setPythonVersion(queryState.pythonVersion || "3.10");
    setCudaVersion(queryState.cudaVersion || "12.0.1");
    setLifecycleScriptTab("paste");
    setLifecycleScriptFile(null);
    setDrawerOpen(false);
    setDrawerMode("configure");
    setTempDrawerContainer(null);
    setAutoFocusScript(false);
  }, [
    queryState.buildMode,
    queryState.cudaVersion,
    queryState.featuredContainer,
    queryState.jupyter,
    queryState.launchableID,
    queryState.pythonVersion,
    selectedLaunchableId,
    shouldRestoreSoftwareFromUrl,
  ]);

  useEffect(() => {
    if (!instanceFromQuery) {
      setSelectedInstanceType(undefined);
      return;
    }

    const found = allInstanceTypes.find((i) => i.type === instanceFromQuery);
    if (found) {
      setSelectedInstanceType(found);
    }
  }, [instanceFromQuery, allInstanceTypes]);

  useEffect(() => {
    if (!selectedInstanceType) {
      setStorage(queryState.storage || "256");
      return;
    }

    if (selectedInstanceType?.elastic_root_volume && queryState.storage) {
      setStorage(queryState.storage);
      return;
    }

    if (selectedInstanceType && queryState.storage) {
      setStorage("256");
    }

    const cloudCreds = getInstanceCloudCreds(selectedInstanceType);
    if (cloudCreds.length > 0) {
      const firstCloudCred = cloudCreds[0];
      if (firstCloudCred.id === "dgxc-gcp-1") {
        setStorage("512");
      }
    }
  }, [selectedInstanceType, queryState.storage]);

  useEffect(() => {
    const cloudCreds = getInstanceCloudCreds(selectedInstanceType);
    if (cloudCreds.length > 0) {
      const cloudCred = cloudCreds[0];
      if (cloudCred.id === "dgxc-gcp-1") {
        setSelectedCloudName("dgxc-gcp-1");
      } else if (cloudCred.tenantType === "isolated") {
        setSelectedCloudName(cloudCred.id);
      } else {
        setSelectedCloudName(allPublicCloudsName);
      }
    }
  }, [selectedInstanceType]);

  const handleNameChange = (val: string) => {
    setEnvironmentName(val);
    if (val === "") {
      setNameFieldErrorMessage("Name required!");
      return;
    }
    const isValid = validateName(val);
    if (!isValid) {
      setNameFieldErrorMessage(
        "Name can only have letters, numbers and dashes.",
      );
      return;
    }
    setNameFieldErrorMessage("");
  };

  const { isEntitled, isLoading: isFreeTierLoading } = useFreeTier();

  useEffect(() => {
    if (!selectedInstanceType || isFreeTierLoading || !orgContext.activeOrgId) {
      return;
    }
    const { withinFreeTier: isStorageWithinFreeTier } = getStoragePricing(
      selectedInstanceType,
      Number(storage),
    );
    const isAllFreeTier =
      isEntitled &&
      isFreeTierInstance(
        getBasePriceAmount(selectedInstanceType, selectedLocation?.name),
      ) &&
      isStorageWithinFreeTier;
    if (isAllFreeTier) {
      setNoCardOnFile(false);
      return;
    }
    handleIsPaymentSetup(selectedCloudName, orgContext.activeOrgId).then(
      (isPaymentSetUp) => setNoCardOnFile(!isPaymentSetUp),
    );
  }, [
    orgContext.activeOrgId,
    cloudCreds,
    selectedCloudName,
    selectedInstanceType,
    selectedLocation,
    isEntitled,
    isFreeTierLoading,
    storage,
  ]);

  const handleSelectedInstanceChange = useCallback(
    (instance: GPUInstanceType | undefined) => {
      if (instance?.type === instanceFromQuery) {
        return;
      }

      if (!instance) {
        setSelectedInstanceType(undefined);
        returnToComputePicker();
        return;
      }

      setSelectedInstanceType(instance);

      const updates: CreateEnvironmentUrlUpdates = {
        gpu: getGpuQueryValueForInstance(instance),
        instanceType: instance.type,
        storage: instance.elastic_root_volume ? storage : undefined,
      };
      if (!locationParam) {
        const filteredLocations = getInstanceLocationsMatching(
          instance,
          pickerAllowedLocations,
        );
        updates.location =
          pickerAllowedLocations && filteredLocations.length === 1
            ? filteredLocations[0]
            : "any";
      } else if (
        locationParam !== "any" &&
        !queryState.customRegion &&
        !isLocationAvailableForInstance(instance, locationParam)
      ) {
        updates.location = "any";
        updates.customRegion = undefined;
      }

      pushCreateEnvironmentSearch(updates);
    },
    [
      instanceFromQuery,
      isLocationAvailableForInstance,
      locationParam,
      pickerAllowedLocations,
      pushCreateEnvironmentSearch,
      queryState.customRegion,
      returnToComputePicker,
      storage,
    ],
  );

  const handleStorageChange = useCallback(
    (nextStorage: string) => {
      setStorage(nextStorage);
      if (selectedInstanceType?.elastic_root_volume) {
        replaceCreateEnvironmentSearch({ storage: nextStorage });
      }
    },
    [replaceCreateEnvironmentSearch, selectedInstanceType],
  );

  const handleContainerChange = useCallback(
    (
      nextContainer: ContainerSelected,
      featuredContainerParams?: FeaturedContainerUrlParams,
    ) => {
      setContainer(nextContainer);
      setSelectedLaunchableId(null);
      replaceCreateEnvironmentSearch(
        getCreateEnvironmentUrlStateFromContainer(
          nextContainer,
          featuredContainerParams,
        ),
      );
    },
    [replaceCreateEnvironmentSearch],
  );

  // Launchpad instances bundle their own k8s, so drop any single-node k8s selection when switching to one.
  useEffect(() => {
    if (
      isLaunchpad &&
      container?.vmBuild?.k8s &&
      !container.vmBuild.k8s.isDisabled
    ) {
      handleContainerChange({ vmBuild: { forceJupyterInstall: true } });
    }
  }, [isLaunchpad, container, handleContainerChange]);

  const handleAnyLocationSelect = useCallback(() => {
    setSelectedLocation(null);
    setRegionPopoverOpen(false);
    setRegionSearch("");
    replaceCreateEnvironmentSearch({
      location: "any",
      customRegion: undefined,
    });
  }, [replaceCreateEnvironmentSearch]);

  const handleLocationSelect = useCallback(
    (location: Location) => {
      setUseCustomRegion(false);
      setCustomRegionValue("");
      setSelectedLocation(location);
      setRegionPopoverOpen(false);
      setRegionSearch("");
      replaceCreateEnvironmentSearch({
        location: location.name,
        customRegion: undefined,
      });
    },
    [replaceCreateEnvironmentSearch],
  );

  const handleCustomRegionToggle = useCallback(
    (checked: boolean | "indeterminate") => {
      const isChecked = checked === true;
      setUseCustomRegion(isChecked);
      if (!isChecked) {
        setCustomRegionValue("");
        setSelectedLocation(null);
        replaceCreateEnvironmentSearch({
          location: "any",
          customRegion: undefined,
        });
      }
    },
    [replaceCreateEnvironmentSearch],
  );

  const handleCustomRegionChange = useCallback(
    (value: string) => {
      setCustomRegionValue(value);
      const trimmedValue = value.trim();
      if (trimmedValue) {
        setSelectedLocation({
          name: trimmedValue,
          description: trimmedValue,
        });
        replaceCreateEnvironmentSearch({
          location: trimmedValue,
          customRegion: true,
        });
      } else {
        setSelectedLocation(null);
        replaceCreateEnvironmentSearch({
          location: undefined,
          customRegion: undefined,
        });
      }
    },
    [replaceCreateEnvironmentSearch],
  );

  const getContainerDisplayName = () => {
    const hasSetupScript = !!(
      container?.vmBuild?.lifeCycleScriptAttr?.script?.trim() ||
      lifecycleScriptFile
    );

    if (container?.vmBuild?.k8s && !container.vmBuild.k8s.isDisabled) {
      if (container.vmBuild.k8s.isDashboardEnabled) {
        return "Single-node Kubernetes w/ Dashboard";
      }
      return "Single-node Kubernetes";
    } else if (container?.vmBuild) {
      const parts: string[] = ["VM Mode"];
      if (container.vmBuild.forceJupyterInstall) {
        parts.push("Jupyter");
      }
      if (hasSetupScript) {
        parts.push("Setup Script");
      }

      if (parts.length === 1) return parts[0];
      if (parts.length === 2) return `${parts[0]} w/ ${parts[1]}`;
      return `${parts[0]} w/ ${parts[1]} & ${parts[2]}`;
    } else if (container?.verbBuild?.containerUrl) {
      if (container.verbBuild.containerUrl === "Default") {
        return `Brev Container: (Python ${pythonVersion} - CUDA ${cudaVersion})`;
      }
      return container.verbBuild.containerUrl;
    } else if (container?.dockerCompose) {
      if (container.dockerCompose.jupyterInstall) {
        return "Docker Compose w/ Jupyter on host";
      }
      return "Docker Compose";
    } else if (container?.customContainer?.containerUrl) {
      return container.customContainer.containerUrl;
    }
    return "Basic Environment";
  };

  const getContainerDescription = () => {
    if (container?.vmBuild?.k8s && !container.vmBuild.k8s.isDisabled) {
      return "Single-node Kubernetes cluster";
    } else if (container?.vmBuild) {
      if (container?.vmBuild?.forceJupyterInstall) {
        return "Raw VM with Jupyter installed.";
      }
      return "Raw VM without Jupyter.";
    } else if (container?.verbBuild?.containerUrl) {
      return "Optimized container for development.";
    } else if (container?.dockerCompose) {
      return "Multi-container Docker application.";
    } else if (container?.customContainer?.containerUrl) {
      return "Custom Docker container.";
    }
    return "Raw VM with Jupyter installed.";
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Modals */}
      {billingContext.billingProfile?.billing_type === "credit" ? (
        <AddCreditsModal
          show={showConnectCloudsOrCardModal}
          setShow={setShowConnectCloudsOrCardModal}
          onCancel={() => setShowConnectCloudsOrCardModal(false)}
          setNoCardOnFile={setNoCardOnFile}
          noCardOnFile={noCardOnFile}
          onSuccess={() => {
            setShowConnectCloudsOrCardModal(false);
          }}
        />
      ) : (
        <ConnectCloudsOrCardModal
          show={showConnectCloudsOrCardModal}
          setShow={setShowConnectCloudsOrCardModal}
          onCancel={() => setShowConnectCloudsOrCardModal(false)}
          setNoCardOnFile={setNoCardOnFile}
          onSuccess={() => {
            setShowConnectCloudsOrCardModal(false);
          }}
          setSelectedCloudName={setSelectedCloudName}
          selectedCloudName={selectedCloudName}
        />
      )}

      <div className="mb-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white pt-2">
              {terminology.createEnvironmentTitle}
            </h1>
            <p className="mt-2 text-neutral-400">
              Select compute, configure software, and deploy.
            </p>
            {queryState.networkId && (
              <p className="mt-2 text-sm text-highlight">
                Target network:{" "}
                <span className="font-medium">
                  {queryState.networkName || queryState.networkId}
                </span>
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="space-y-6">
        <div className="flex flex-col">
          {!selectedInstanceType && (
            <FilterBar
              allGPUInstances={instanceTypesContext.GPUInstancesAvailable}
              allCPUInstances={instanceTypesContext.CPUInstancesAvailable}
              setDisplayGPUInstances={setDisplayGPUInstances}
              setDisplayCPUInstances={setDisplayCPUInstances}
              containerSelected={container}
              onFilterStateChange={(
                persistent,
                flexiblePorts,
                flexibleStorage,
              ) => {
                setPersistentOnly(persistent);
                setFlexiblePortControlOnly(flexiblePorts);
                setFlexibleStorageOnly(flexibleStorage);
              }}
              locationName={selectedLocation?.name ?? null}
              regionFilter={regionFilter}
              setRegionFilter={setRegionFilter}
            />
          )}
        </div>

        <ComputeSelector
          allGPUInstances={instanceTypesContext.GPUInstancesAvailable}
          cpuInstances={
            displayCPUInstances ?? instanceTypesContext.CPUInstancesAvailable
          }
          isLoadingInstances={instanceTypesContext.isInstancesLoading}
          selectedInstance={selectedInstanceType}
          setSelectedInstance={handleSelectedInstanceChange}
          setStorage={handleStorageChange}
          storage={storage}
          containerSelected={container}
          selectedCloudName={selectedCloudName}
          displayGPUInstances={displayGPUInstances}
          persistentOnly={persistentOnly}
          flexiblePortControlOnly={flexiblePortControlOnly}
          flexibleStorageOnly={flexibleStorageOnly}
          locationName={selectedLocation?.name ?? null}
          pickerAllowedLocations={pickerAllowedLocations}
        />

        {!!selectedInstanceType && (
          <div className="mt-8 pt-8 border-t border-neutral-800">
            {/* Region & Software Configuration Row */}
            <div className="flex flex-col md:flex-row md:items-stretch gap-6 mb-8">
              {/* Region Selector */}
              <div className="md:w-[400px] shrink-0 flex flex-col">
                <h2 className="text-lg font-bold text-white mb-4">
                  <div className="flex items-center gap-2">
                    <MapPinIcon className="w-5 h-5" />
                    Region
                    <InfoTooltip text="Regions vary across providers, so the available region values may differ depending on the selected instance type." />
                  </div>
                </h2>
                <div className="p-6 rounded-xl border border-neutral-800 bg-neutral-900 flex-1">
                  <div className="flex items-center gap-2 mb-3">
                    <Checkbox
                      checked={useCustomRegion}
                      onCheckedChange={handleCustomRegionToggle}
                    />
                    <label className="text-sm text-secondary cursor-pointer">
                      Custom
                    </label>
                  </div>
                  {useCustomRegion ? (
                    <Input
                      type="text"
                      placeholder="Enter custom region (e.g. us-east-1)"
                      value={customRegionValue}
                      onChange={(e) => {
                        handleCustomRegionChange(e.target.value);
                      }}
                      className="w-full"
                    />
                  ) : (
                    <Popover
                      open={regionPopoverOpen}
                      onOpenChange={(open) => {
                        setRegionPopoverOpen(open);
                        if (!open) setRegionSearch("");
                      }}
                    >
                      <PopoverTrigger asChild>
                        <button className="flex items-center justify-between w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-white hover:bg-zinc-800 transition-colors">
                          <span className="truncate">
                            {selectedLocation
                              ? regionToReadable[selectedLocation.name]
                                ? `${regionToReadable[selectedLocation.name].city}, ${regionToReadable[selectedLocation.name].country} (${selectedLocation.name})`
                                : selectedLocation.description ||
                                  selectedLocation.name
                              : "Any"}
                          </span>
                          <ChevronDownIcon className="w-4 h-4 ml-2 shrink-0 text-zinc-400" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent
                        className="p-0 w-[var(--radix-popover-trigger-width)]"
                        align="start"
                      >
                        <div className="p-2 border-b border-zinc-800">
                          <Input
                            type="text"
                            placeholder="Search by city, country, or region code..."
                            value={regionSearch}
                            onChange={(e) => setRegionSearch(e.target.value)}
                            className="w-full text-sm"
                            autoFocus
                          />
                        </div>
                        <div className="max-h-[280px] overflow-y-auto p-1">
                          <button
                            className={`w-full text-left px-3 py-2 text-sm rounded-md transition-colors ${
                              !selectedLocation
                                ? "bg-zinc-800 text-white"
                                : "text-zinc-300 hover:bg-zinc-800/50"
                            }`}
                            onClick={() => {
                              handleAnyLocationSelect();
                            }}
                          >
                            Any
                          </button>
                          {groupedLocations.map(({ geo, locations }) => (
                            <div key={geo}>
                              <div className="flex items-center gap-2 px-3 pt-4 pb-1.5 mt-1 first:mt-0 border-t border-zinc-800 first:border-t-0">
                                <span className="text-xs font-bold uppercase tracking-wider text-zinc-300">
                                  {geo}
                                </span>
                                <div className="h-px flex-1 bg-zinc-700" />
                              </div>
                              {locations.map((location) => {
                                const info = regionToReadable[location.name];
                                const isSelected =
                                  selectedLocation?.name === location.name;
                                return (
                                  <button
                                    key={location.name}
                                    className={`w-full text-left px-3 py-1.5 text-sm rounded-md transition-colors ${
                                      isSelected
                                        ? "bg-zinc-800 text-white"
                                        : "text-zinc-300 hover:bg-zinc-800/50"
                                    }`}
                                    onClick={() => {
                                      handleLocationSelect(location);
                                    }}
                                  >
                                    {info ? (
                                      <span>
                                        {info.city}, {info.country}{" "}
                                        <span className="text-zinc-500 font-mono text-xs">
                                          ({location.name})
                                        </span>
                                      </span>
                                    ) : (
                                      location.description || location.name
                                    )}
                                  </button>
                                );
                              })}
                            </div>
                          ))}
                          {groupedLocations.length === 0 && regionSearch && (
                            <div className="px-3 py-4 text-sm text-zinc-500 text-center">
                              No regions match "{regionSearch}"
                            </div>
                          )}
                        </div>
                      </PopoverContent>
                    </Popover>
                  )}
                </div>
              </div>

              {/* Software Configuration */}
              <div className="flex-1 min-w-0 flex flex-col">
                <h2 className="text-lg font-bold text-white mb-4">
                  Software Configuration
                </h2>
                <div className="p-6 rounded-xl border border-neutral-800 bg-neutral-900 flex-1 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                  {selectedLaunchable ? (
                    <div className="flex items-start gap-4 flex-1">
                      <div className="p-[3px] rounded-lg shrink-0 bg-gradient-to-br from-[#76b900] via-[#5c9200] to-[#1a2e05] shadow-lg shadow-green-900/10">
                        <div className="p-2.5 bg-neutral-900 rounded-[5px]">
                          <Rocket className="w-6 h-6 text-[#76b900]" />
                        </div>
                      </div>
                      <div className="flex flex-col flex-1">
                        <a
                          href={`/launchable/deploy/now?launchableID=${selectedLaunchableId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="group flex items-center gap-1.5 text-lg font-semibold text-white hover:text-highlight transition-colors"
                        >
                          {selectedLaunchable.name}
                          <ExternalLink className="w-4 h-4 opacity-50 group-hover:opacity-100 transition-opacity" />
                        </a>
                        <p className="text-neutral-400 text-sm mt-1">
                          {selectedLaunchable.description ||
                            "Launchable Configuration"}
                        </p>
                      </div>
                      <Button
                        variant="secondary"
                        onClick={() => {
                          setDrawerMode("browse");
                          setDrawerOpen(true);
                        }}
                        className="shrink-0 self-center"
                      >
                        <div className="flex items-center gap-2">
                          <Settings2 className="w-4 h-4" />
                          Edit
                        </div>
                      </Button>
                    </div>
                  ) : (
                    <div className="flex flex-col sm:flex-row flex-1 items-start md:items-center gap-4">
                      <div className="flex items-start gap-4 flex-1">
                        <div className="p-3 bg-neutral-800 rounded-lg shrink-0">
                          <HardDrive className="w-6 h-6 text-neutral-100" />
                        </div>
                        <div className="flex flex-col flex-1">
                          <div>
                            <h2 className="text-lg font-semibold text-white">
                              {getContainerDisplayName()}
                            </h2>
                            <p className="text-neutral-400 text-sm mt-1">
                              {getContainerDescription()}
                            </p>
                          </div>
                          {/* Show pills only for default VM configuration */}
                          {getContainerDisplayName() ===
                            "VM Mode w/ Jupyter" && (
                            <div className="flex flex-wrap gap-2 mt-3">
                              <Badge
                                variant="neutral"
                                className="cursor-pointer transition-all hover:bg-neutral-800 hover:ring-neutral-400 hover:text-neutral-100"
                                onClick={() => {
                                  setDrawerMode("configure");
                                  setTempDrawerContainer({
                                    vmBuild: {
                                      forceJupyterInstall: true,
                                      lifeCycleScriptAttr: {
                                        script: DEFAULT_LIFECYCLE_SCRIPT,
                                      },
                                    },
                                  });
                                  setAutoFocusScript(true);
                                  setDrawerOpen(true);
                                }}
                              >
                                Add a setup script
                              </Badge>
                              <Badge
                                variant="neutral"
                                className="cursor-pointer transition-all hover:bg-neutral-800 hover:ring-neutral-400 hover:text-neutral-100"
                                onClick={() => {
                                  setDrawerMode("configure");
                                  setTempDrawerContainer({
                                    verbBuild: { containerUrl: "Default" },
                                  });
                                  setDrawerOpen(true);
                                }}
                              >
                                Switch to Docker
                              </Badge>
                              {!isLaunchpad && (
                                <Badge
                                  variant="neutral"
                                  className="cursor-pointer transition-all hover:bg-neutral-800 hover:ring-neutral-400 hover:text-neutral-100"
                                  onClick={() => {
                                    setDrawerMode("configure");
                                    setTempDrawerContainer({
                                      vmBuild: {
                                        forceJupyterInstall: false,
                                        k8s: {
                                          isDisabled: false,
                                          isDashboardEnabled: true,
                                        },
                                      },
                                    });
                                    setDrawerOpen(true);
                                  }}
                                >
                                  Switch to Kubernetes
                                </Badge>
                              )}
                              <Badge
                                variant="neutral"
                                className="cursor-pointer transition-all hover:bg-neutral-800 hover:ring-neutral-400 hover:text-neutral-100"
                                onClick={() => {
                                  setDrawerMode("browse");
                                  setDrawerOpen(true);
                                }}
                              >
                                Select a Launchable
                              </Badge>
                            </div>
                          )}
                        </div>
                      </div>
                      <Button
                        variant="secondary"
                        onClick={() => {
                          setDrawerMode("configure");
                          setDrawerOpen(true);
                        }}
                        className="shrink-0 self-center"
                      >
                        <div className="flex items-center gap-2">
                          <Settings2 className="w-4 h-4" />
                          Edit
                        </div>
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <DeploymentSection
              selectedInstanceType={selectedInstanceType}
              container={container}
              setContainer={() => {}} // Read-only container config from this view
              environmentName={environmentName}
              handleNameChange={handleNameChange}
              nameFieldErrorMessage={nameFieldErrorMessage}
              storage={storage}
              selectedCloudName={selectedCloudName}
              loading={loading}
              setLoading={setLoading}
              setShowFinalDeployMessage={setShowFinalDeployMessage}
              portMappings={portMappings}
              pythonVersion={pythonVersion}
              setPythonVersion={setPythonVersion}
              cudaVersion={cudaVersion}
              setCudaVersion={setCudaVersion}
              lifecycleScriptTab={lifecycleScriptTab}
              setLifecycleScriptTab={setLifecycleScriptTab}
              lifecycleScriptFile={lifecycleScriptFile}
              setLifecycleScriptFile={setLifecycleScriptFile}
              setShowConnectCloudsOrCardModal={setShowConnectCloudsOrCardModal}
              noCardOnFile={noCardOnFile}
              availableLocations={availableLocations}
              selectedLocation={selectedLocation}
              setSelectedLocation={setSelectedLocation}
              instanceCount={instanceCount}
              setInstanceCount={setInstanceCount}
              hideContainerConfig={true}
              hideRegionSelector={true}
              launchableConfig={
                selectedLaunchableId ? { id: selectedLaunchableId } : undefined
              }
              networkId={queryState.networkId}
            />
          </div>
        )}
      </div>

      <Drawer
        open={drawerOpen}
        onOpenChange={(open) => {
          setDrawerOpen(open);
          if (!open) {
            // Reset temp container when drawer closes to ensure next open uses current container unless specified
            setTimeout(() => {
              setTempDrawerContainer(null);
              setAutoFocusScript(false);
            }, 300);
          }
        }}
      >
        <DrawerContent className="sm:max-w-3xl w-full">
          <DrawerHeader>
            <DrawerTitle>
              <div className="flex flex-row items-center text-lg font-bold">
                Configure your GPU environment
              </div>
            </DrawerTitle>
          </DrawerHeader>
          <DrawerBody>
            <Tabs
              value={drawerMode}
              onValueChange={(val) =>
                setDrawerMode(val as "configure" | "browse")
              }
              className="w-full flex flex-col"
            >
              <div className="border-b border-zinc-800 mb-4">
                <TabsList>
                  <TabsTrigger value="configure">
                    Custom Configuration
                  </TabsTrigger>
                  <TabsTrigger value="browse">Launchables</TabsTrigger>
                </TabsList>
              </div>

              <div className="flex flex-col flex-1 overflow-hidden">
                <TabsContent value="configure" className="mt-0 h-full">
                  <DrawerContainerSelector
                    closeDrawer={() => setDrawerOpen(false)}
                    selectedContainer={tempDrawerContainer || container}
                    setSelectedContainer={handleContainerChange}
                    pythonVersion={pythonVersion}
                    setPythonVersion={setPythonVersion}
                    cudaVersion={cudaVersion}
                    setCudaVersion={setCudaVersion}
                    lifecycleScriptTab={lifecycleScriptTab}
                    setLifecycleScriptTab={setLifecycleScriptTab}
                    lifecycleScriptFile={lifecycleScriptFile}
                    setLifecycleScriptFile={setLifecycleScriptFile}
                    autoFocusScript={autoFocusScript}
                    selectedGpuName={
                      selectedInstanceType?.supported_gpus?.[0]?.name
                    }
                    selectedInstanceArchitecture={
                      selectedInstanceType?.architecture
                    }
                    showRapidsOption={selectedInstanceHasGpu}
                    disableKubernetes={isLaunchpad}
                    kubernetesDisabledReason="Kubernetes is preconfigured for Launchpad instances. No additional cluster setup is required."
                  />
                </TabsContent>
                <TabsContent value="browse" className="mt-0 h-full">
                  <BrowseLaunchables
                    selectedLaunchableId={selectedLaunchableId}
                    onSelect={(launchable) => {
                      const id =
                        launchable.environment_id || (launchable as any).id;
                      setSelectedLaunchableId(id);
                      replaceCreateEnvironmentSearch(
                        getCreateEnvironmentUrlStateFromLaunchable(id),
                      );

                      const build =
                        launchable.build || (launchable as any).buildRequest;
                      // Optimistically set the selected launchable so it shows in the UI immediately
                      setSelectedLaunchable({
                        id: id,
                        name: launchable.name || "",
                        description:
                          launchable.meta?.description ||
                          (launchable as any).description ||
                          "",
                        buildRequest: build,
                      } as any);

                      // Also update container state optimistically if build info is present
                      if (build) {
                        setContainer({
                          vmBuild: build.vmBuild || build.vm,
                          dockerCompose:
                            build.dockerCompose || build.docker_compose,
                        } as any);
                      }

                      // Auto-populate environment name from launchable
                      if (launchable.name) {
                        const normalizedName = launchable.name
                          .replace(/[^a-zA-Z0-9]/g, "-")
                          .toLowerCase();
                        const randomString = uuid().substring(0, 6);
                        setEnvironmentName(`${normalizedName}-${randomString}`);
                      }

                      setDrawerOpen(false);
                    }}
                    defaultGPU={selectedInstanceType?.supported_gpus?.[0]?.name}
                    defaultCloudProvider={
                      selectedInstanceType
                        ? isBrevCloud(selectedInstanceType.provider)
                          ? "Any"
                          : GetProviderName(selectedInstanceType)
                        : selectedCloudName === allPublicCloudsName
                          ? "Any"
                          : selectedCloudName
                    }
                  />
                </TabsContent>
              </div>
            </Tabs>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </div>
  );
};
